import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { SubjectSlug } from '@jeraisonne/db';
import { supabase } from '@/lib/supabase';

export type TutorResult = {
  id: string;
  display_name: string;
  avatar_url: string | null;
};

export function useSearchTutors(namePattern: string) {
  return useQuery({
    queryKey: ['search-tutors', namePattern],
    enabled: namePattern.trim().length >= 2,
    staleTime: 30_000,
    queryFn: async (): Promise<TutorResult[]> => {
      const { data, error } = await supabase.rpc('search_tutors_by_name', {
        p_name_pattern: namePattern.trim(),
        p_limit: 8,
      });
      if (error) throw new Error(error.message);
      return (data as TutorResult[]) ?? [];
    },
  });
}

export type RequestTutorInput = {
  tutorId: string;
  /** Matière(s) demandées (migration 0227) — vide = toutes matières. */
  subjects: SubjectSlug[];
};

export function useRequestTutor(studentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ tutorId, subjects }: RequestTutorInput) => {
      // Check for an existing link first
      const { data: existing } = await supabase
        .from('tutor_links')
        .select('id, status')
        .eq('tutor_id', tutorId)
        .eq('student_id', studentId)
        .maybeSingle();

      if (existing?.status === 'active') {
        throw new Error('Tu es déjà lié(e) à ce tuteur.');
      }
      if (existing?.status === 'pending') {
        throw new Error('Une demande est déjà en attente avec ce tuteur.');
      }

      if (!existing) {
        const { error } = await supabase.from('tutor_links').insert({
          tutor_id: tutorId,
          student_id: studentId,
          status: 'pending',
          initiated_by: 'student',
          subjects,
        });
        if (error) throw new Error(error.message);
      } else {
        // revoked → reactivate (relancée par l'élève)
        const { error } = await supabase
          .from('tutor_links')
          .update({ status: 'pending', initiated_by: 'student', subjects })
          .eq('id', existing.id);
        if (error) throw new Error(error.message);
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pending-tutor-invitations', studentId] });
    },
  });
}
